import { useState, useRef, useCallback, useEffect } from 'react';
import { storiesApi } from '../api/client';

// Block type definitions
const BLOCK_TYPES = [
  { type: 'paragraph',   icon: '¶',  label: 'Paragraph' },
  { type: 'heading',     icon: 'H2', label: 'Heading' },
  { type: 'subheading',  icon: 'H3', label: 'Subheading' },
  { type: 'image',       icon: '🖼',  label: 'Image' },
  { type: 'split_left',  icon: '◧',  label: 'Image Left + Text' },
  { type: 'split_right', icon: '◨',  label: 'Image Right + Text' },
  { type: 'quote',       icon: '"',  label: 'Quote' },
  { type: 'divider',     icon: '—',  label: 'Divider' },
];

let idCounter = 1;
function makeId() { return `b${idCounter++}`; }

function makeBlock(type) {
  const base = { id: makeId(), type };
  switch (type) {
    case 'paragraph':   return { ...base, content: '' };
    case 'heading':     return { ...base, content: '' };
    case 'subheading':  return { ...base, content: '' };
    case 'image':       return { ...base, image_url: '', caption: '' };
    case 'split_left':  return { ...base, image_url: '', caption: '', text: '' };
    case 'split_right': return { ...base, image_url: '', caption: '', text: '' };
    case 'quote':       return { ...base, content: '', attribution: '' };
    case 'divider':     return base;
    default:            return { ...base, content: '' };
  }
}

// Auto-resize textarea
function AutoTextarea({ value, onChange, placeholder, className, minRows = 1, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = ref.current.scrollHeight + 'px';
    }
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      rows={minRows}
      style={{ overflow: 'hidden' }}
      {...rest}
    />
  );
}

// Image upload zone component
function ImageUploadZone({ imageUrl, onUpload, label = 'Click to upload image' }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await storiesApi.uploadImage(file);
      onUpload(res.data.image_url);
    } catch (err) {
      alert('Image upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (imageUrl) {
    return (
      <div style={{ position: 'relative' }}>
        <img src={imageUrl} alt="" style={{ width: '100%', borderRadius: 'var(--radius)', maxHeight: 400, objectFit: 'cover' }} />
        <button
          onClick={() => onUpload('')}
          style={{
            position: 'absolute', top: 8, right: 8,
            background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none',
            borderRadius: '50%', width: 28, height: 28, cursor: 'pointer',
            fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          title="Remove image"
        >×</button>
      </div>
    );
  }

  return (
    <div className={`image-upload-zone ${uploading ? 'uploading' : ''}`} onClick={() => inputRef.current?.click()}>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
      <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.4 }}>🖼</div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
        {uploading ? 'Uploading…' : label}
      </div>
      {!uploading && (
        <div style={{ fontFamily: 'var(--font-sans)', fontSize: '0.75rem', color: 'var(--text-faint)', marginTop: '0.25rem' }}>
          JPG, PNG, WebP accepted
        </div>
      )}
    </div>
  );
}

// Individual block renderers (edit mode)
function ParagraphBlock({ block, onChange }) {
  return (
    <AutoTextarea
      value={block.content}
      onChange={e => onChange({ ...block, content: e.target.value })}
      placeholder="Start writing…"
      className="block-textarea"
      minRows={2}
    />
  );
}

function HeadingBlock({ block, onChange }) {
  return (
    <input
      type="text" value={block.content}
      onChange={e => onChange({ ...block, content: e.target.value })}
      placeholder="Heading…"
      className="block-heading-input"
    />
  );
}

function SubheadingBlock({ block, onChange }) {
  return (
    <input
      type="text" value={block.content}
      onChange={e => onChange({ ...block, content: e.target.value })}
      placeholder="Subheading…"
      className="block-subheading-input"
    />
  );
}

function ImageBlock({ block, onChange }) {
  return (
    <div>
      <ImageUploadZone
        imageUrl={block.image_url}
        onUpload={url => onChange({ ...block, image_url: url })}
      />
      {block.image_url && (
        <input
          type="text" value={block.caption}
          onChange={e => onChange({ ...block, caption: e.target.value })}
          placeholder="Add a caption (optional)…"
          className="block-caption-input"
          style={{ marginTop: '0.4rem' }}
        />
      )}
    </div>
  );
}

function SplitBlock({ block, onChange }) {
  const isLeft = block.type === 'split_left';
  const imageCol = (
    <div>
      <ImageUploadZone
        imageUrl={block.image_url}
        onUpload={url => onChange({ ...block, image_url: url })}
        label="Upload image"
      />
      {block.image_url && (
        <input
          type="text" value={block.caption}
          onChange={e => onChange({ ...block, caption: e.target.value })}
          placeholder="Caption (optional)…"
          className="block-caption-input"
          style={{ marginTop: '0.4rem' }}
        />
      )}
    </div>
  );
  const textCol = (
    <AutoTextarea
      value={block.text}
      onChange={e => onChange({ ...block, text: e.target.value })}
      placeholder="Write text alongside the image…"
      className="block-textarea"
      minRows={4}
    />
  );
  return (
    <div className="split-editor">
      {isLeft ? <>{imageCol}{textCol}</> : <>{textCol}{imageCol}</>}
    </div>
  );
}

function QuoteBlock({ block, onChange }) {
  return (
    <div style={{ borderLeft: '4px solid var(--green)', paddingLeft: '0' }}>
      <AutoTextarea
        value={block.content}
        onChange={e => onChange({ ...block, content: e.target.value })}
        placeholder="Enter a quote or pull-quote…"
        className="block-quote-textarea"
        minRows={2}
      />
      <input
        type="text" value={block.attribution}
        onChange={e => onChange({ ...block, attribution: e.target.value })}
        placeholder="— Attribution (optional)"
        style={{
          width: '100%', border: 'none', outline: 'none', padding: '0.3rem 1rem',
          fontFamily: 'var(--font-sans)', fontSize: '0.8rem', color: 'var(--text-light)',
          background: 'transparent',
        }}
      />
    </div>
  );
}

function DividerBlock() {
  return <hr style={{ border: 'none', borderTop: '2px solid var(--border)', margin: '0.5rem 0' }} />;
}

function BlockContent({ block, onChange }) {
  switch (block.type) {
    case 'paragraph':   return <ParagraphBlock block={block} onChange={onChange} />;
    case 'heading':     return <HeadingBlock block={block} onChange={onChange} />;
    case 'subheading':  return <SubheadingBlock block={block} onChange={onChange} />;
    case 'image':       return <ImageBlock block={block} onChange={onChange} />;
    case 'split_left':
    case 'split_right': return <SplitBlock block={block} onChange={onChange} />;
    case 'quote':       return <QuoteBlock block={block} onChange={onChange} />;
    case 'divider':     return <DividerBlock />;
    default:            return null;
  }
}

// Add-block menu
function AddBlockMenu({ onAdd, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  return (
    <div ref={ref} className="block-type-menu" style={{ position: 'relative', zIndex: 200 }}>
      {BLOCK_TYPES.map(bt => (
        <button key={bt.type} className="block-type-btn" onClick={() => { onAdd(bt.type); onClose(); }}>
          <span className="block-type-icon">{bt.icon}</span>
          {bt.label}
        </button>
      ))}
    </div>
  );
}

// Main BlockEditor
export default function BlockEditor({ blocks, onChange }) {
  const [activeMenu, setActiveMenu] = useState(null); // index where to insert

  const updateBlock = useCallback((index, newBlock) => {
    const next = [...blocks];
    next[index] = newBlock;
    onChange(next);
  }, [blocks, onChange]);

  const addBlock = useCallback((afterIndex, type) => {
    const next = [...blocks];
    next.splice(afterIndex + 1, 0, makeBlock(type));
    onChange(next);
  }, [blocks, onChange]);

  const removeBlock = useCallback(index => {
    if (blocks.length <= 1) return; // keep at least one block
    const next = blocks.filter((_, i) => i !== index);
    onChange(next);
  }, [blocks, onChange]);

  const moveBlock = useCallback((index, direction) => {
    const next = [...blocks];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  }, [blocks, onChange]);

  return (
    <div className="block-editor" style={{ position: 'relative' }}>
      {blocks.map((block, index) => (
        <div key={block.id}>
          {/* Block item */}
          <div className="block-item" style={{ position: 'relative', paddingRight: '56px' }}>
            <BlockContent block={block} onChange={newBlock => updateBlock(index, newBlock)} />
            {/* Controls */}
            <div style={{
              position: 'absolute', right: 0, top: 4,
              display: 'flex', flexDirection: 'column', gap: 2,
            }}>
              <button className="block-ctrl-btn" onClick={() => moveBlock(index, -1)} title="Move up" disabled={index === 0}>↑</button>
              <button className="block-ctrl-btn" onClick={() => moveBlock(index, 1)} title="Move down" disabled={index === blocks.length - 1}>↓</button>
              <button className="block-ctrl-btn danger" onClick={() => removeBlock(index)} title="Remove block">×</button>
            </div>
          </div>

          {/* Add block bar between items */}
          <div style={{ position: 'relative', height: 24, display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)', opacity: 0 }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0'}
            />
            <button
              className="add-block-btn"
              onClick={() => setActiveMenu(activeMenu === index ? null : index)}
              title="Add block"
            >+</button>
            <div style={{ flex: 1, height: 1, background: 'var(--border)', opacity: 0 }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0'}
            />
            {activeMenu === index && (
              <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', zIndex: 300 }}>
                <AddBlockMenu
                  onAdd={type => addBlock(index, type)}
                  onClose={() => setActiveMenu(null)}
                />
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Empty state */}
      {blocks.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-light)' }}>
          <div style={{ fontSize: '0.9rem', fontFamily: 'var(--font-sans)', marginBottom: '1rem' }}>
            Start your story by adding a block
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => setActiveMenu(-1)}>+ Add block</button>
          {activeMenu === -1 && (
            <div style={{ position: 'relative', marginTop: 8 }}>
              <AddBlockMenu onAdd={type => { addBlock(-1, type); setActiveMenu(null); }} onClose={() => setActiveMenu(null)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
